// Global Instructions Rule Applied!
import { createSlice, createSelector, type PayloadAction } from '@reduxjs/toolkit'
import { CONFIG_MODULES } from '../../app_modules/configuration/configModules.ts'

export type TabType = 'table' | 'form' | 'page' | 'dashboard' | 'file-viewer'

export interface Tab {
  id: string
  key: string
  title: string
  path: string
  type: TabType
  module: string
  closeable: boolean
  isDefault: boolean
  /** Form tabs: the record they show; null for a "new" form. */
  recordId?: string | null
  isNew?: boolean
  /** True for /<module>/forms/full/<id> tabs (vs. a table tab with a drawer). */
  isFullForm?: boolean
  /** The tab that opened this form: closing returns there. */
  openerKey?: string | null
  /** file-viewer tabs only */
  fileUrl?: string | null
  fileName?: string | null
}

export interface TabsState {
  tabs: Tab[]
  activeTabKey: string | null
  /** The table/page tab each module opens on its route, keyed by module. */
  defaultTabs: Record<string, Tab>
}

export interface AddTabPayload {
  id: string
  key: string
  title: string
  path: string
  type?: TabType
  module: string
  closeable?: boolean
  recordId?: string | null
  fileUrl?: string | null
  fileName?: string | null
}

/**
 * Tab Slice - Manages dynamic tab navigation state
 *
 * Features:
 * - Generic tab management for any module
 * - Configurable default/non-closeable tabs
 * - Support for different tab types (table, form, etc.)
 * - Integration with routing system
 */

const initialState: TabsState = {
  tabs: [
    {
      id: 'deal-pipeline-table',
      key: 'deal-pipeline-table',
      title: 'Deal Pipeline',
      path: '/deals/pipeline',
      type: 'table',
      module: 'deal-pipeline',
      closeable: false,
      isDefault: true
    }
  ],
  activeTabKey: 'deal-pipeline-table',
  // Configuration for default tabs per module
  defaultTabs: {
    deals: {
      id: 'deals-table',
      key: 'deals-table',
      title: 'Deals',
      path: '/deals',
      type: 'table',
      module: 'deals',
      closeable: true,
      isDefault: false
    },
    'deal-pipeline': {
      id: 'deal-pipeline-table',
      key: 'deal-pipeline-table',
      title: 'Deal Pipeline',
      path: '/deals/pipeline',
      type: 'table',
      module: 'deal-pipeline',
      closeable: false,
      isDefault: true
    },
    'deal-execution': {
      id: 'deal-execution-table',
      key: 'deal-execution-table',
      title: 'Deal Execution',
      path: '/deal-execution',
      type: 'table',
      module: 'deal-execution',
      closeable: true,
      isDefault: false
    },
    companies: {
      id: 'companies-table',
      key: 'companies-table',
      title: 'Companies',
      path: '/companies',
      type: 'table',
      module: 'companies',
      closeable: false,
      isDefault: true
    },
    contacts: {
      id: 'contacts-table',
      key: 'contacts-table',
      title: 'Contacts',
      path: '/contacts',
      type: 'table',
      module: 'contacts',
      closeable: false,
      isDefault: true
    },
    fundraising: {
      id: 'fundraising-table',
      key: 'fundraising-table',
      title: 'Prospects',
      path: '/fundraising',
      type: 'table',
      module: 'fundraising',
      closeable: false,
      isDefault: true
    },
    'fundraising-pipeline': {
      id: 'fundraising-pipeline-table',
      key: 'fundraising-pipeline-table',
      title: 'Fundraising',
      path: '/fundraising/pipeline',
      type: 'table',
      module: 'fundraising-pipeline',
      closeable: false,
      isDefault: true
    },
    prospects: {
      id: 'prospects-table',
      key: 'prospects-table',
      title: 'Prospects',
      path: '/prospects',
      type: 'table',
      module: 'prospects',
      closeable: true,
      isDefault: false
    },
    dashboard: {
      id: 'dashboard-main',
      key: 'dashboard-main',
      title: 'Dashboard',
      path: '/dashboard',
      type: 'dashboard',
      module: 'dashboard',
      closeable: false,
      isDefault: true
    },
    'asset-transactions': {
      id: 'asset-transactions-table',
      key: 'asset-transactions-table',
      title: 'Asset Transactions',
      path: '/asset-transactions',
      type: 'table',
      module: 'asset-transactions',
      closeable: true,
      isDefault: false
    },
    rounds: {
      id: 'rounds-table',
      key: 'rounds-table',
      title: 'Rounds',
      path: '/rounds',
      type: 'table',
      module: 'rounds',
      closeable: true,
      isDefault: false
    },
    tranches: {
      id: 'tranches-table',
      key: 'tranches-table',
      title: 'Tranches',
      path: '/tranches',
      type: 'table',
      module: 'tranches',
      closeable: true,
      isDefault: false
    },
    downloads: {
      id: 'downloads-page',
      key: 'downloads-page',
      title: 'Downloads',
      path: '/downloads',
      type: 'page',
      module: 'downloads',
      closeable: false,
      isDefault: true
    },
    reports: {
      id: 'reports-page',
      key: 'reports-page',
      title: 'Reports',
      path: '/reports',
      type: 'page',
      module: 'reports',
      closeable: false,
      isDefault: false
    },
    funds: {
      id: 'funds-table',
      key: 'funds-table',
      title: 'Funds',
      path: '/funds',
      type: 'table',
      module: 'funds',
      closeable: true,
      isDefault: false
    },
    investments: {
      id: 'investments-table',
      key: 'investments-table',
      title: 'Investments',
      path: '/investments',
      type: 'table',
      module: 'investments',
      closeable: true,
      isDefault: false
    },
    'fund-transactions': {
      id: 'fund-transactions-table',
      key: 'fund-transactions-table',
      title: 'Fund Transactions',
      path: '/fund-transactions',
      type: 'table',
      module: 'fund-transactions',
      closeable: true,
      isDefault: false
    },
    'capital-calls': {
      id: 'capital-calls-table',
      key: 'capital-calls-table',
      title: 'Capital Calls',
      path: '/capital-calls',
      type: 'table',
      module: 'capital-calls',
      closeable: true,
      isDefault: false
    },
    distributions: {
      id: 'distributions-table',
      key: 'distributions-table',
      title: 'Distributions',
      path: '/distributions',
      type: 'table',
      module: 'distributions',
      closeable: true,
      isDefault: false
    },
    'journal-entries': {
      id: 'journal-entries-table',
      key: 'journal-entries-table',
      title: 'Journal Entries',
      path: '/journal-entries',
      type: 'table',
      module: 'journal-entries',
      closeable: true,
      isDefault: false
    },
    'investor-statements': {
      id: 'investor-statements-table',
      key: 'investor-statements-table',
      title: 'Investor Statements',
      path: '/investor-statements',
      type: 'table',
      module: 'investor-statements',
      closeable: true,
      isDefault: false
    },
    'consolidated-funds': {
      id: 'consolidated-funds-table',
      key: 'consolidated-funds-table',
      title: 'Consolidated Funds',
      path: '/consolidated-funds',
      type: 'table',
      module: 'consolidated-funds',
      closeable: true,
      isDefault: false
    },
    'fee-booking': {
      id: 'fee-booking-table',
      key: 'fee-booking-table',
      title: 'Fee Booking',
      path: '/fee-booking',
      type: 'table',
      module: 'fee-booking',
      closeable: true,
      isDefault: false
    },
    'interest-accruals': {
      id: 'interest-accruals-table',
      key: 'interest-accruals-table',
      title: 'Interest Accruals',
      path: '/interest-accruals',
      type: 'table',
      module: 'interest-accruals',
      closeable: true,
      isDefault: false
    },
    'capital-call-position': {
      id: 'capital-call-position-table',
      key: 'capital-call-position-table',
      title: 'Capital Call Position',
      path: '/capital-call-position',
      type: 'table',
      module: 'capital-call-position',
      closeable: true,
      isDefault: false
    },
    agents: {
      id: 'agents-table',
      key: 'agents-table',
      title: 'Agent Content',
      path: '/agents',
      type: 'table',
      module: 'agents',
      closeable: true,
      isDefault: false
    },
    topics: {
      id: 'topics-table',
      key: 'topics-table',
      title: 'Topic Management',
      path: '/topics',
      type: 'table',
      module: 'topics',
      closeable: true,
      isDefault: false
    },
    'topic-subscriptions': {
      id: 'topic-subscriptions-table',
      key: 'topic-subscriptions-table',
      title: 'Topic Subscriptions',
      path: '/topic-subscriptions',
      type: 'table',
      module: 'topic-subscriptions',
      closeable: true,
      isDefault: false
    },
    'distribution-lists': {
      id: 'distribution-lists-table',
      key: 'distribution-lists-table',
      title: 'Distribution Lists',
      path: '/distribution-lists',
      type: 'table',
      module: 'distribution-lists',
      closeable: true,
      isDefault: false
    },
    'send-communication': {
      id: 'send-communication-table',
      key: 'send-communication-table',
      title: 'Send Communication',
      path: '/send-communication',
      type: 'table',
      module: 'send-communication',
      closeable: true,
      isDefault: false
    },
    // LP Allocation (Fundraising) and My Drafts (MY WORKSPACE): module keys
    // match the cfg.menu_items `key` so parseRoute → module-map lands here.
    'lp-allocation': {
      id: 'lp-allocation-table',
      key: 'lp-allocation-table',
      title: 'LP Allocation',
      path: '/lp-allocation',
      type: 'page',
      module: 'lp-allocation',
      closeable: true,
      isDefault: false
    },
    // FUND MANAGEMENT → LPs (team-name check over the LP Allocation row set).
    lps: {
      id: 'lps-table',
      key: 'lps-table',
      title: 'LPs',
      path: '/lps',
      type: 'page',
      module: 'lps',
      closeable: true,
      isDefault: false
    },
    'my-drafts': {
      id: 'my-drafts-table',
      key: 'my-drafts-table',
      title: 'My Drafts',
      path: '/my-drafts',
      type: 'page',
      module: 'my-drafts',
      closeable: true,
      isDefault: false
    },
    // Reporting → Period Highlights (LP agent programme Phase 1): the list
    // page; a set opens as a form tab (/period-highlights/forms/full/:id).
    'period-highlights': {
      id: 'period-highlights-table',
      key: 'period-highlights-table',
      title: 'Period Highlights',
      path: '/period-highlights',
      type: 'page',
      module: 'period-highlights',
      closeable: true,
      isDefault: false
    },
    'bulk-add-prospects': {
      id: 'bulk-add-prospects-table',
      key: 'bulk-add-prospects-table',
      title: 'Bulk Prospects',
      path: '/bulk-add-prospects',
      type: 'table',
      module: 'bulk-add-prospects',
      closeable: true,
      isDefault: false
    },
    // Email Phase 2: compose / draft full-form list + Settings (signature +
    // delegations). Both live under the COMMUNICATION menu category; the
    // module keys match the cfg.menu_items `key` column (/email,
    // /email-settings) so parseRoute → module-map fallthrough lands here.
    email: {
      id: 'email-table',
      key: 'email-table',
      title: 'Email',
      path: '/email',
      type: 'table',
      module: 'email',
      closeable: true,
      isDefault: false
    },
    'email-settings': {
      id: 'email-settings-table',
      key: 'email-settings-table',
      title: 'Email Settings',
      path: '/email-settings',
      type: 'page',
      module: 'email-settings',
      closeable: true,
      isDefault: false
    },
    // My Agents — the personal agent screen (Fund Management → MY WORKSPACE).
    // Module key matches the cfg.menu_items `key` ('my-agents', href
    // /my-agents) so parseRoute → module-map fallthrough lands here.
    'my-agents': {
      id: 'my-agents-table',
      key: 'my-agents-table',
      title: 'My Agents',
      path: '/my-agents',
      type: 'page',
      module: 'my-agents',
      closeable: true,
      isDefault: false
    },
    // Alias for the screen's former route, kept for one release so existing
    // bookmarks still open (owner 2026-09-02). It has no cfg.menu_items row —
    // it exists only so parseRoute finds a defaultTabs entry: a module without
    // one makes addModuleTab a silent no-op and the URL renders blank.
    'agent-access': {
      id: 'agent-access-table',
      key: 'agent-access-table',
      title: 'My Agents',
      path: '/agent-access',
      type: 'page',
      module: 'agent-access',
      closeable: true,
      isDefault: false
    },
    // Configuration screen (manage cfg data — Industries first). Lives under
    // the SETTINGS menu category; module key matches the cfg.menu_items `key`
    // ('configuration', href /configuration) so parseRoute → module-map
    // fallthrough lands here.
    configuration: {
      id: 'configuration-table',
      key: 'configuration-table',
      title: 'Configuration',
      path: '/configuration',
      type: 'page',
      module: 'configuration',
      closeable: true,
      isDefault: false
    },
    // Operations-area copy of Configuration (distinct route so the per-area
    // sub-menu works: this copy shows all sections incl. Pipelines)
    'configuration-operations': {
      id: 'configuration-operations-table',
      key: 'configuration-operations-table',
      title: 'Configuration',
      path: '/configuration-operations',
      type: 'page',
      module: 'configuration-operations',
      closeable: true,
      isDefault: false
    },
    // Configuration AREA: one tab per config panel, generated from the
    // CONFIG_MODULES registry (adding a panel there is enough — addModuleTab
    // silently no-ops for any module missing from defaultTabs). The two
    // legacy combined entries above stay for back-compat during transition.
    ...Object.fromEntries(
      CONFIG_MODULES.map((m): [string, Tab] => [
        m.moduleKey,
        {
          id: `${m.moduleKey}-table`,
          key: `${m.moduleKey}-table`,
          title: m.title,
          path: `/${m.moduleKey}`,
          type: 'page',
          module: m.moduleKey,
          closeable: true,
          isDefault: false
        }
      ])
    )
  }
}

const tabSlice = createSlice({
  name: 'tabs',
  initialState,
  reducers: {
    // Add a new tab or activate existing one
    addTab: (state, action: PayloadAction<AddTabPayload>) => {
      const {
        id,
        key,
        title,
        path,
        type = 'form',
        module,
        closeable = true,
        recordId = null,
        fileUrl = null,
        fileName = null
      } = action.payload

      // Check if tab already exists
      const existingTab = state.tabs.find((tab) => tab.key === key)

      if (existingTab) {
        // Tab exists, just activate it
        state.activeTabKey = key
      } else {
        // Create new tab
        const newTab: Tab = {
          id,
          key,
          title,
          path,
          type,
          module,
          closeable,
          recordId,
          isDefault: false
        }

        // Add file viewer specific properties if provided
        if (type === 'file-viewer') {
          newTab.fileUrl = fileUrl
          newTab.fileName = fileName
        }

        state.tabs.push(newTab)
        state.activeTabKey = key
      }
    },

    // Morph a "new"-record form tab into the edit tab for the record it just
    // created, IN PLACE. The old flow (openFormInTab + removeTab) left the
    // opener tab active for one paint between the navigation and the route
    // effect re-adding the edit tab — a visible flash of another screen.
    // Keeping the tab and rewriting key/path/recordId means the pane stays
    // active; the caller navigates to the edit path afterwards and the route
    // effect finds the tab already active. The pane remounts (content is
    // keyed by tab.key) and cold-loads the created record.
    transformFormTab: (
      state,
      action: PayloadAction<{ key: string; module: string; recordId: string | null | undefined; title?: string }>
    ) => {
      const { key, module, recordId, title } = action.payload
      const tab = state.tabs.find((t) => t.key === key)
      if (!tab || !recordId) return
      const newKey = `${module}-form-edit-${recordId}`
      const existing = state.tabs.find((t) => t.key === newKey)
      if (existing) {
        // an edit tab for this record is already open — use it, drop the new-tab
        state.tabs = state.tabs.filter((t) => t.key !== key)
        if (title) existing.title = title
        state.activeTabKey = newKey
        return
      }
      tab.key = newKey
      tab.id = newKey
      tab.path = `/${module}/forms/full/${recordId}`
      // the created record may belong to another module than the "new" form
      // (the New commitment form creates a consolidated commitment too)
      tab.module = module
      tab.recordId = recordId
      tab.isNew = false
      if (title) tab.title = title
      state.tabs.forEach((t) => {
        if (t.openerKey === key) t.openerKey = newKey
      })
      if (state.activeTabKey === key) state.activeTabKey = newKey
    },

    // Remove a tab
    removeTab: (state, action: PayloadAction<{ key: string | null }>) => {
      const { key } = action.payload
      const tabIndex = state.tabs.findIndex((tab) => tab.key === key)

      if (tabIndex !== -1) {
        const removedTab = state.tabs[tabIndex]

        // Don't allow removing non-closeable tabs
        if (!removedTab.closeable) {
          return
        }

        // Remove the tab
        state.tabs.splice(tabIndex, 1)

        // Collapse the opener chain: tabs opened FROM the removed tab now
        // point at the removed tab's own opener (e.g. the new->edit form
        // flip on create, where the 'new' tab vanishes immediately).
        state.tabs.forEach((tab) => {
          if (tab.openerKey === key) {
            tab.openerKey = removedTab.openerKey || null
          }
        })

        // If we're removing the active tab, switch to another tab
        if (state.activeTabKey === key) {
          // Prefer the tab that opened this one (form tabs record it), so
          // Cancel/close returns where the user came from...
          const opener = removedTab.openerKey ? state.tabs.find((tab) => tab.key === removedTab.openerKey) : null
          if (opener) {
            state.activeTabKey = opener.key
          } else if (state.tabs.length > 0) {
            // ...else the tab to the left, or the first tab if we removed the first one
            const newActiveIndex = Math.max(0, tabIndex - 1)
            state.activeTabKey = state.tabs[newActiveIndex].key
          } else {
            // No tabs left, reset to empty state
            state.activeTabKey = null
          }
        }
      }
    },

    // Set the active tab
    setActiveTab: (state, action: PayloadAction<{ key: string }>) => {
      const { key } = action.payload
      if (state.tabs.find((tab) => tab.key === key)) {
        state.activeTabKey = key
      }
    },

    // Add module default tab and activate it
    addModuleTab: (state, action: PayloadAction<{ module: string }>) => {
      const { module } = action.payload
      const defaultTab = state.defaultTabs[module]

      if (defaultTab) {
        // Check if tab already exists
        const existingTab = state.tabs.find((tab) => tab.key === defaultTab.key)

        if (existingTab) {
          // Tab exists, just activate it
          state.activeTabKey = defaultTab.key
        } else {
          // Add the default tab for this module
          // Only the deal-pipeline table is truly non-closeable (the configured default)
          const newTab: Tab = {
            ...defaultTab,
            closeable: module !== 'deal-pipeline' // Only deal-pipeline default tab is non-closeable
          }
          state.tabs.push(newTab)
          state.activeTabKey = defaultTab.key
        }
      }
    },

    // Add a form tab (for full forms)
    addFormTab: (
      state,
      action: PayloadAction<{
        module: string
        recordId?: string | null
        title?: string
        isNew?: boolean
        query?: string
      }>
    ) => {
      const { module, recordId, title, isNew = false, query = '' } = action.payload

      // Generate unique key for form tab. New-record tabs carry their query
      // string (prefill context, e.g. ?deal=…&company=… from "+ Transaction"
      // on a deal's Execution tab) in both key and path: a distinct key per
      // booking context means each prefill gets its own freshly-mounted form
      // instead of re-activating a stale singleton "new" tab, and the query
      // in the stored path survives tab switches and refreshes. Edit tabs
      // stay query-free so one-shot deep links (?tab=execution) don't stick.
      const formType = isNew ? 'new' : 'edit'
      const querySuffix = isNew && query ? `?${query}` : ''
      const key = `${module}-form-${formType}-${recordId || 'new'}${querySuffix}`
      const path = `/${module}/forms/full/${recordId || 'new'}${querySuffix}`

      // Create tab title
      const tabTitle = isNew ? `New ${title || module}` : `Edit ${title || module} (${recordId})`

      // Check if tab already exists
      const existingTab = state.tabs.find((tab) => tab.key === key)

      if (existingTab) {
        // Tab exists, just activate it. Re-opening from a different tab
        // re-points the opener there, so closing returns where you came from.
        if (state.activeTabKey && state.activeTabKey !== key) {
          existingTab.openerKey = state.activeTabKey
        }
        state.activeTabKey = key
      } else {
        // Create new form tab - these are always closeable
        const newTab: Tab = {
          id: key,
          key,
          title: tabTitle,
          path,
          type: 'form',
          module,
          closeable: true, // Form tabs are always closeable
          recordId: recordId || null,
          isNew,
          isFullForm: true, // This indicates it's a full form tab
          isDefault: false,
          // The tab that opened this form: closing/cancelling returns here
          // rather than to whichever tab happens to sit to the left.
          openerKey: state.activeTabKey || null
        }

        state.tabs.push(newTab)
        state.activeTabKey = key
      }
    },

    // Update tab title (useful for when a new record gets an ID)
    updateTabTitle: (state, action: PayloadAction<{ key: string; title: string }>) => {
      const { key, title } = action.payload
      const tab = state.tabs.find((tab) => tab.key === key)
      if (tab) {
        tab.title = title
      }
    },

    // Update tab path (useful for redirects after save)
    updateTabPath: (state, action: PayloadAction<{ key: string; path: string }>) => {
      const { key, path } = action.payload
      const tab = state.tabs.find((tab) => tab.key === key)
      if (tab) {
        tab.path = path
      }
    },

    // Close all tabs except default tabs
    closeAllTabs: (state) => {
      state.tabs = state.tabs.filter((tab) => !tab.closeable || tab.isDefault)

      // Set active tab to first remaining tab
      if (state.tabs.length > 0) {
        state.activeTabKey = state.tabs[0].key
      } else {
        state.activeTabKey = null
      }
    },

    // Reset tabs to initial state
    resetTabs: () => {
      return initialState
    },

    // Update default tab configuration
    updateDefaultTab: (state, action: PayloadAction<{ module: string; tabConfig: Partial<Tab> }>) => {
      const { module, tabConfig } = action.payload
      state.defaultTabs[module] = { ...state.defaultTabs[module], ...tabConfig }
    }
  }
})

export const {
  addTab,
  removeTab,
  setActiveTab,
  addModuleTab,
  addFormTab,
  transformFormTab,
  updateTabTitle,
  updateTabPath
} = tabSlice.actions
const { closeAllTabs, resetTabs, updateDefaultTab } = tabSlice.actions

type TabsRoot = { tabs: TabsState }

// Selectors
export const selectTabs = (state: TabsRoot) => state.tabs.tabs
export const selectActiveTabKey = (state: TabsRoot) => state.tabs.activeTabKey
export const selectActiveTab = (state: TabsRoot) => {
  const activeKey = state.tabs.activeTabKey
  return state.tabs.tabs.find((tab) => tab.key === activeKey) || null
}
// Memoized selector to get tab by key
// Uses createSelector for memoization to prevent unnecessary re-renders
const selectTabByKey = createSelector(
  [(state: TabsRoot) => state.tabs.tabs, (state: TabsRoot, key: string) => key],
  (tabs, key) => tabs.find((tab) => tab.key === key) || null
)

// Memoized selector to get tabs by module
// Uses createSelector for memoization to prevent unnecessary re-renders
const selectTabsByModule = createSelector(
  [(state: TabsRoot) => state.tabs.tabs, (state: TabsRoot, module: string) => module],
  (tabs, module) => tabs.filter((tab) => tab.module === module)
)

// Memoized selector to get default tab for module
// Uses createSelector for memoization to prevent unnecessary re-renders
const selectDefaultTabForModule = createSelector(
  [(state: TabsRoot) => state.tabs.defaultTabs, (state: TabsRoot, module: string) => module],
  (defaultTabs, module) => defaultTabs[module] || null
)

export default tabSlice.reducer
